import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Checkbox, InputAdornment, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination,
  TableRow, TextField, ToggleButton, ToggleButtonGroup, Typography, alpha, useTheme,
} from '@mui/material';
import { ReceiptLongOutlined, Search } from '@mui/icons-material';
import { JOURNAL_SOURCE_LABEL, ReconciliationLine } from '../../../../types/finance';
import { useTimezone } from '../../../../hooks/useTimezone';
import EmptyState from '../../../common/EmptyState';
import Amount from '../../ledger/Amount';

type Show = 'all' | 'uncleared' | 'cleared';
type Direction = 'all' | 'in' | 'out';

/**
 * The bank account's lines up to the statement date. Tick what appears on
 * the statement; the header box ticks every line in view. Read-only for a
 * completed reconciliation.
 */
const ReconciliationLinesTable: React.FC<{
  lines: ReconciliationLine[];
  editable: boolean;
  onToggle?: (lineIds: string[], cleared: boolean) => void;
  onOpenEntry: (entryId: string) => void;
  initialShow?: Show;
  emptyText?: string;
}> = ({ lines, editable, onToggle, onOpenEntry, initialShow = 'all', emptyText = 'No lines on this account up to the statement date.' }) => {
  const theme = useTheme();
  const { formatDate } = useTimezone();
  const [show, setShow] = useState<Show>(initialShow);
  const [direction, setDirection] = useState<Direction>('all');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(50);

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return lines.filter(l =>
      (show === 'all' || (show === 'cleared') === l.cleared)
      && (direction === 'all' || (direction === 'in' ? l.inPaisa > 0 : l.outPaisa > 0))
      && (!needle || l.entryNo.toLowerCase().includes(needle) || l.memo.toLowerCase().includes(needle)
        || String(l.inPaisa || l.outPaisa).includes(needle.replace(/[,.]/g, ''))));
  }, [lines, show, direction, q]);

  useEffect(() => { setPage(0); }, [show, direction, q]);

  const pageRows = visible.slice(page * limit, page * limit + limit);
  const ticked = visible.filter(l => l.cleared).length;
  const all = visible.length > 0 && ticked === visible.length;
  const toggleAll = () => onToggle?.(visible.filter(l => l.cleared === all).map(l => l._id), !all);

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <ToggleButtonGroup exclusive size="small" value={show} onChange={(_, v) => v && setShow(v)}
          sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 1.5 } }}>
          <ToggleButton value="all">All ({lines.length})</ToggleButton>
          <ToggleButton value="uncleared">Not ticked ({lines.filter(l => !l.cleared).length})</ToggleButton>
          <ToggleButton value="cleared">Ticked ({lines.filter(l => l.cleared).length})</ToggleButton>
        </ToggleButtonGroup>
        <ToggleButtonGroup exclusive size="small" value={direction} onChange={(_, v) => v && setDirection(v)}
          sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 1.5 } }}>
          <ToggleButton value="all">In and out</ToggleButton>
          <ToggleButton value="in">Money in</ToggleButton>
          <ToggleButton value="out">Money out</ToggleButton>
        </ToggleButtonGroup>
        <TextField
          size="small"
          placeholder="Voucher, description or amount…"
          value={q}
          onChange={e => setQ(e.target.value)}
          sx={{ flex: 1, minWidth: 200 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" color="action" /></InputAdornment> }}
        />
      </Stack>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: alpha(theme.palette.text.primary, 0.02) }}>
              <TableCell padding="checkbox">
                {editable && (
                  <Checkbox size="small" checked={all} indeterminate={ticked > 0 && !all} disabled={!visible.length} onChange={toggleAll}
                    inputProps={{ 'aria-label': all ? 'Untick every line in view' : 'Tick every line in view' }} />
                )}
              </TableCell>
              <TableCell sx={{ width: 120 }}>Date</TableCell>
              <TableCell sx={{ width: 160 }}>Voucher</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="right" sx={{ width: 140 }}>Money in</TableCell>
              <TableCell align="right" sx={{ width: 140 }}>Money out</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pageRows.map(l => (
              <TableRow
                key={l._id}
                hover
                selected={l.cleared}
                onClick={editable ? () => onToggle?.([l._id], !l.cleared) : undefined}
                sx={{ cursor: editable ? 'pointer' : 'default', '&.Mui-selected': { bgcolor: alpha(theme.palette.success.main, 0.05) }, '&.Mui-selected:hover': { bgcolor: alpha(theme.palette.success.main, 0.09) } }}
              >
                <TableCell padding="checkbox">
                  <Checkbox size="small" checked={l.cleared} disabled={!editable} color="success" tabIndex={-1} />
                </TableCell>
                <TableCell><Typography variant="body2">{formatDate(l.entryDate)}</Typography></TableCell>
                <TableCell>
                  <Typography
                    variant="body2" fontWeight={700} component="span"
                    onClick={e => { e.stopPropagation(); onOpenEntry(l.entryId); }}
                    sx={{ fontFamily: 'monospace', cursor: 'pointer', '&:hover': { textDecoration: 'underline', color: 'primary.main' } }}
                  >
                    {l.entryNo}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" noWrap sx={{ maxWidth: 420 }}>{l.memo}</Typography>
                  <Typography variant="caption" color="text.secondary">{JOURNAL_SOURCE_LABEL[l.sourceType] || l.sourceType}</Typography>
                </TableCell>
                <TableCell align="right"><Amount paisa={l.inPaisa} color={l.inPaisa ? 'success.dark' : undefined} /></TableCell>
                <TableCell align="right"><Amount paisa={l.outPaisa} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {!visible.length && (
        <Box sx={{ p: 3 }}>
          <EmptyState icon={<ReceiptLongOutlined />} title={lines.length ? 'No lines match' : 'Nothing to tick'}
            description={lines.length ? 'Change a filter to see more.' : emptyText} dense />
        </Box>
      )}

      {visible.length > 25 && (
        <TablePagination
          component="div"
          count={visible.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={limit}
          onRowsPerPageChange={e => { setLimit(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[25, 50, 100, 250]}
        />
      )}
    </Paper>
  );
};

export default ReconciliationLinesTable;
